import { useQueries } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { TDisplayOrderQuadrantAbbreviations } from '../modules/patientList/patientStrip/patientStripOdontogram/types';
import { useShouldHideHuddle } from '../modules/patientList/useShouldHideHuddle';

import { makeAPIRequestReactQuery } from './api';
import { usePreferences } from './usePreferences';
import type { TCariesDepth } from './useSegmentations';

/**
 * @deprecated use useVisitSummaries
 */
export const useFmxSummaries = (
  args?: TFmxSummaryArgs,
  enabledOnHuddleRedesign?: boolean,
) => {
  const { data: prefs } = usePreferences();
  const hideHuddle = useShouldHideHuddle();
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  const estimatedVisitIds = args
    ? args.flatMap((evs) => {
        const evs1 = evs
          ?.filter((ev) => !!ev.estimatedVisitId)
          .map((ev) => ev.estimatedVisitId);
        return evs1;
      })
    : [];

  const groupedEstimatedVisits: (number | null | undefined)[][] = [];
  for (let i = 0; i < estimatedVisitIds?.length; i += 10) {
    const firstIndex = i;
    const lastIndex =
      i + 10 < estimatedVisitIds?.length ? i + 10 : estimatedVisitIds.length;
    groupedEstimatedVisits.push(estimatedVisitIds.slice(firstIndex, lastIndex));
  }

  return useQueries({
    queries: groupedEstimatedVisits.length
      ? groupedEstimatedVisits.map((visits) => {
          return {
            queryKey: ['fmx-summaries', visits, currentPractice],
            staleTime: 10 * 1000,
            queryFn: () => {
              if (!visits?.length) {
                return [];
              }
              return makeAPIRequestReactQuery<TFmxSummaries>(
                `fmx-summaries`,
                { estimatedVisitIds: visits },
                'get',
                {
                  headers: {
                    practice: currentPractice,
                    org: currentOrg,
                  },
                },
              );
            },
            enabled: Boolean(
              prefs &&
                (enabledOnHuddleRedesign || hideHuddle) &&
                visits?.length &&
                currentPractice &&
                currentOrg,
            ),
          };
        })
      : [],
  });
};

type TFmxSummaryArgs = (
  | {
      estimatedVisitId?: number | null;
    }[]
  | undefined
)[];

type TPlannedTreatment = {
  treatmentDate: Date | null;
  treatmentId: string;
  cdtCode: string | null;
  status: 'OTHER' | 'COMPLETED' | 'PLANNED' | 'REJECTED' | 'SCHEDULED';
  toothNum: string | null;
  toothRange: string | null;
};

export type TFmxSummaryTooth = {
  dmf: number;
  numEnamelCaries: number;
  deepestCariesDepth: TCariesDepth;
  plannedTreatments?: {
    plannedCrownTreatments?: null | TPlannedTreatment[];
    plannedPotentialProactiveTreatments?: null | TPlannedTreatment[];
  };
  /**
   * this is the highest-priority status for all this indicator's treatments
   * completed > scheduled > planned
   *
   * if it's undefined then we didn't have any.
   */
  crownTreatmentStatus?: TPlannedTreatment['status'];
  /**
   * should we suggest a crown?
   * There's no treatments that are planned,scheduled,completed
   * for the tooth?
   */
  crownSuggested?: boolean;
  /**
   * should we suggest enamel regen?
   * There's no treatments that are planned,scheduled,completed
   * for the tooth?
   */
  enamelRegenSuggested?: boolean;
  /**
   * this is just what we've determined out of analyses without considering treatments.
   */
  potentialEnamelRegen?: boolean;
  /**
   * this is just what we've determined out of analyses without considering treatments.
   */
  potentialCrown?: boolean;
  /**
   * this is the highest-priority status for all this indicator's treatments
   * completed > scheduled > planned
   *
   * if it's undefined then we didn't have any.
   */
  enamelRegenTreatmentStatus?: TPlannedTreatment['status'];
  potentialIncipientImageId?: number;
  potentialCrownImageId?: number;
  potentialCrownImageIds?: number[];
  hasDecay?: boolean;
  hasPrl?: boolean;
  /**
   * @deprecated use crownSuggested
   */
  hasCrown?: boolean;
  /**
   * @deprecated use enamelRegenSuggested
   */
  hasIncipient?: boolean;
  /**
   * @deprecated use plannedTreatments.plannedPotentialProactiveTreatments
   */
  incipientTreatment?: null | TPlannedTreatment;
  /**
   * @deprecated use plannedTreatments.plannedCrownTreatments
   */
  plannedCrownTreatment?: null | TPlannedTreatment;
  boneLevelMesial?: number;
  boneLevelDistal?: number;
  hasCalculus?: boolean;
  boneLevelSourceImageType?: string;
};

export type TFmxSummaryQuad = {
  numOfTeethWithCalculus: number;
  rblSeverities: {
    [tier: string]: number;
  };

  srpTreatments: TPlannedTreatment[];
  /**
   * this is the highest-priority status for all this indicator's treatments
   * completed > scheduled > planned
   *
   * if it's undefined then we didn't have any.
   */
  srpTreatmentStatus?: TPlannedTreatment['status'];
  /**
   * should we suggest SRP?
   * There's no treatments that are planned,scheduled,completed
   * for the quad or a general-SRP treatment.
   */
  srpSuggested?: boolean;
  /**
   * this is just what we've determined out of analyses without considering treatments.
   */
  potentialSrp?: boolean;
  /**
   * @deprecated use srpSuggested
   */

  srpImageManifestIds: number;

  hasSrp?: boolean;
  srpReason?: string;
};

export type TFmxSummary = {
  [x: string]: any;
  estimatedVisitId?: number;
  hasDentalPhotos?: boolean;
  summary?: {
    [toothId: string]: TFmxSummaryTooth;
  };
  quadSummary?: {
    [key in TDisplayOrderQuadrantAbbreviations]: TFmxSummaryQuad;
  };
};

export type TFmxSummaries = TFmxSummary[] | undefined;
